/**
 * Page Resources
 */

import {
  McpServer,
  ResourceTemplate,
} from "@modelcontextprotocol/sdk/server/mcp.js";
import { McpError, ErrorCode } from "@modelcontextprotocol/sdk/types.js";

import { fetchBlobFromUrl } from "../common/formatters/page.js";
import { registerTrackedResource } from "../logging.js";
import { ServerContext } from "../types/server.js";

export function registerPageImageResource(
  server: McpServer,
  context: ServerContext,
) {
  if (!context.features["OAK-37_mcp_image_resources"]) {
    return;
  }

  registerTrackedResource(
    server,
    "page-image",
    new ResourceTemplate("zeroheight://image/{+imageUrl}", { list: undefined }),
    {
      title: "Styleguide page image",
      description:
        "Fetch a single image from a styleguide page by its URL. Preferred over `get-page-images` when only specific images are needed. Image resource URIs are embedded in the content returned by `get-page`. Construct the resource URI by appending the image URL to `zeroheight://image/` (e.g. `zeroheight://image/https://example.com/image.png`).",
    },
    async (uri, variables) => {
      const raw = variables.imageUrl;
      if (typeof raw !== "string" || raw.length === 0) {
        throw new McpError(ErrorCode.InvalidParams, "No image URL provided.");
      }
      let parsedUrl: URL;
      try {
        parsedUrl = new URL(raw);
      } catch {
        throw new McpError(ErrorCode.InvalidParams, "Invalid image URL.");
      }
      if (parsedUrl.protocol !== "https:") {
        throw new McpError(
          ErrorCode.InvalidParams,
          "Only HTTPS image URLs are supported.",
        );
      }
      const image = await fetchBlobFromUrl(raw);
      if (!image) {
        throw new McpError(
          ErrorCode.InternalError,
          `Could not fetch image: ${raw}`,
        );
      }
      return {
        contents: [
          {
            uri: uri.toString(),
            blob: image.blob,
            mimeType: image.mimeType,
          },
        ],
      };
    },
  );
}
